interface AuthenticationData {
  accessToken?: string
  refreshToken?: string
  idToken?: string
  newDeviceMetadata?: {
    deviceKey: string
    deviceGroupKey: string
  }
}

interface StandardLocalStorageService {
  getAuthenticationData: () => AuthenticationData | null
  updateAuthenticationData: (data: Partial<AuthenticationData>) => AuthenticationData | null
  getDarkModeValue: () => boolean
  setDarkModeValue: (darkMode: boolean) => boolean
}

export class LocalStorageService implements StandardLocalStorageService {
  getAuthenticationData = (): AuthenticationData | null => {
    const rawData = localStorage.getItem('authenticationData')
    if (!rawData) {
      return null
    }

    const data = JSON.parse(rawData)

    const authenticationData: AuthenticationData = {}

    if (data.accessToken) {
      authenticationData.accessToken = data.accessToken
    }

    if (data.refreshToken) {
      authenticationData.refreshToken = data.refreshToken
    }

    if (data.idToken) {
      authenticationData.idToken = data.idToken
    }

    if (data.newDeviceMetadata?.deviceKey) {
      if (authenticationData.newDeviceMetadata) {
        authenticationData.newDeviceMetadata.deviceKey = data.newDeviceMetadata.deviceKey
      } else {
        authenticationData.newDeviceMetadata = {
          deviceKey: data.newDeviceMetadata.deviceKey,
          deviceGroupKey: '',
        }
      }
    }

    if (data.newDeviceMetadata?.deviceGroupKey) {
      if (authenticationData.newDeviceMetadata) {
        authenticationData.newDeviceMetadata.deviceGroupKey = data.newDeviceMetadata.deviceGroupKey
      } else {
        authenticationData.newDeviceMetadata = {
          deviceGroupKey: data.newDeviceMetadata.deviceGroupKey,
          deviceKey: '',
        }
      }
    }

    return authenticationData
  }

  updateAuthenticationData = (input: Partial<AuthenticationData> | null): AuthenticationData | null => {
    const authenticationData = this.getAuthenticationData()

    if (!input) {
      localStorage.removeItem('authenticationData')
      return null
    }

    if (!authenticationData) {
      const data = {
        accessToken: input.accessToken,
        refreshToken: input.refreshToken,
        idToken: input.idToken,
        newDeviceMetadata: input.newDeviceMetadata ? {
          deviceKey: input.newDeviceMetadata?.deviceKey,
          deviceGroupKey: input.newDeviceMetadata?.deviceGroupKey,
        } : undefined,
      }
      this.setAuthenticationDataInLocalStorage(data)
      return data
    }

    if (input.accessToken) {
      authenticationData.accessToken = input.accessToken
    }

    if (input.refreshToken) {
      authenticationData.refreshToken = input.refreshToken
    }

    if (input.idToken) {
      authenticationData.idToken = input.idToken
    }

    if (input.newDeviceMetadata?.deviceKey) {
      if (authenticationData.newDeviceMetadata) {
        authenticationData.newDeviceMetadata.deviceKey = input.newDeviceMetadata.deviceKey
      } else {
        authenticationData.newDeviceMetadata = {
          deviceKey: input.newDeviceMetadata.deviceKey,
          deviceGroupKey: '',
        }
      }
    }

    if (input.newDeviceMetadata?.deviceGroupKey) {
      if (authenticationData.newDeviceMetadata) {
        authenticationData.newDeviceMetadata.deviceGroupKey = input.newDeviceMetadata.deviceGroupKey
      } else {
        authenticationData.newDeviceMetadata = {
          deviceKey: '',
          deviceGroupKey: input.newDeviceMetadata.deviceGroupKey,
        }
      }
    }

    this.setAuthenticationDataInLocalStorage(authenticationData)
    return authenticationData
  }

  private setAuthenticationDataInLocalStorage = (data: AuthenticationData) => {
    localStorage.setItem('authenticationData', JSON.stringify(data))
  }

  getDarkModeValue = (): boolean => {
    const rawDarkMode = localStorage.getItem('darkMode')
    if (!rawDarkMode)
      return false
    return rawDarkMode === 'true'
  }

  setDarkModeValue = (darkMode: boolean): boolean => {
    localStorage.setItem('darkMode', darkMode.toString())
    return darkMode
  }
}

